import { useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  MenuItem,
  Typography,
  Box,
  Switch,
} from "@mui/material";
import { Formik, Form } from "formik";
import { toString } from "lodash";
import { useConfirm } from "material-ui-confirm";
import { useSnackbar } from "notistack";
import { ReactElement, useEffect } from "react";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import z from "zod";

import {
  AllSubaccountSettingsSchema,
  subaccountSettingDefinitions,
  SubaccountSettingKey,
} from "@stw/feature-flags";
import { DEFAULT_AUTO_MMS_BREAKPOINT } from "@stw/sms-text-encoding";

import {
  EditTenantAutoMmsBreakpointMutation,
  EditTenantAutoMmsBreakpointMutationVariables,
  EditTenantSettingsMutation,
  EditTenantSettingsMutationVariables,
  GetTenantForStaffQuery,
  GetTenantForStaffQueryVariables,
  ListSubaccountOwnerFieldsQuery,
  ReleaseAssignmentsMutationVariables,
  ReleaseTenantPhoneNumbersMutation,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import Table from "~frontend/components/Table";
import { CampaignMetadataFieldsSection } from "~frontend/components/campaignMetadata/CampaignMetadataFieldsSection";
import {
  FormikSelect,
  FormikTextField,
  FormikErrorBanner,
  submitErrorField,
  FormikSubmitButton,
} from "~frontend/components/forms";
import { RhfSwitchField } from "~frontend/components/forms/react-hook-form-components";
import {
  BULK_UPDATE_SUBACCOUNT_SETTINGS,
  LIST_SUBACCOUNT_OWNER_FIELDS,
} from "~frontend/graphql/subaccounts";
import {
  EDIT_TENANT_AUTO_MMS_BREAKPOINT,
  EDIT_TENANT_SETTINGS,
  GET_TENANT_FOR_STAFF,
  RELEASE_TENANT_PHONE_NUMBERS,
} from "~frontend/graphql/tenant";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import {
  useCurrentUserIsStaff,
  useTenant,
} from "~frontend/providers/LoggedInUserProvider";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useNonCacheClearingApolloClient } from "~frontend/providers/NonCacheClearingApolloClientProvider";
import theme, { palette } from "~frontend/theme";
import { formatDateTime } from "~frontend/util/dateFormat";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import { jobInProgress } from "~frontend/util/jobs";
import useMutation from "~frontend/util/useMutation";

import { DataToWinOptInTable } from "./DataToWinOptInTable";
import { PricingSection } from "./pricing/PricingSection";

const SubaccountSettingsFormSchema = z.object({
  selectedSetting: z.enum(
    Object.keys(subaccountSettingDefinitions) as [SubaccountSettingKey]
  ),
  bulkValue: z.boolean(),
  subaccountValues: z.record(z.string(), AllSubaccountSettingsSchema.partial()),
});

type SubaccountSettingsForm = z.infer<typeof SubaccountSettingsFormSchema>;

function SubaccountSettingsTable({
  subaccounts,
  selectedSetting,
  title,
}: {
  subaccounts: ListSubaccountOwnerFieldsQuery["listSubaccounts"];
  selectedSetting: SubaccountSettingKey;
  title: string;
}): ReactElement {
  const snackbar = useSnackbar();
  const formatErrorMessage = useErrorMessageFormatter();
  const [saveSettings] = useMutation(BULK_UPDATE_SUBACCOUNT_SETTINGS, {
    refetchQueries: [LIST_SUBACCOUNT_OWNER_FIELDS],
    awaitRefetchQueries: true,
  });

  const {
    control,
    watch,
    getValues,
    setValue,
    handleSubmit,
    formState,
    register,
    reset,
  } = useForm({
    resolver: zodResolver(SubaccountSettingsFormSchema),
    defaultValues: {
      selectedSetting: selectedSetting,
      bulkValue: false,
      subaccountValues: subaccounts.reduce(
        (acc, subaccount) => {
          const settings = subaccount.settings || {};
          acc[subaccount.id] = {
            [selectedSetting]: settings[selectedSetting],
          };
          return acc;
        },
        {} as Record<string, Record<string, any>>
      ),
    },
  });

  const currentSettingDef = subaccountSettingDefinitions[selectedSetting];
  const isCurrentSettingBoolean =
    currentSettingDef.type._def.typeName === "ZodBoolean";

  const handleBulkChange = (checked: boolean) => {
    const newValues = structuredClone(getValues("subaccountValues"));
    subaccounts.forEach(subaccount => {
      if (!newValues[subaccount.id]) {
        newValues[subaccount.id] = {};
      }
      newValues[subaccount.id][selectedSetting] = checked;
    });
    setValue("subaccountValues", newValues);
  };

  async function bulkUpdateSettings(values: SubaccountSettingsForm) {
    const settingName = values.selectedSetting;

    const valuesBySubaccount = Object.entries(values.subaccountValues).map(
      ([subaccountId, value]) => ({
        subaccountId,
        value: value[settingName],
      })
    );

    const { errors } = await saveSettings({
      variables: {
        input: {
          settingName,
          valuesBySubaccount,
        },
      },
    });

    if (errors) {
      snackbar.enqueueSnackbar(
        `Failed to save settings: ${formatErrorMessage(errors)}`,
        {
          variant: "error",
        }
      );
    } else {
      reset({
        selectedSetting,
        bulkValue: false,
        subaccountValues: values.subaccountValues,
      });
    }
  }

  const allSubaccountValues = watch("subaccountValues");

  const currentColumn = {
    key: selectedSetting,
    title: currentSettingDef.name,
    renderValue: (row: { id: string; settings: Record<string, any> }) => {
      const currentValue =
        allSubaccountValues[row.id]?.[selectedSetting] ??
        row.settings[selectedSetting];

      if (isCurrentSettingBoolean) {
        return (
          <Switch
            checked={!!currentValue}
            color="primary"
            {...register(`subaccountValues.${row.id}.${selectedSetting}`)}
          />
        );
      }
      return toString(currentValue);
    },
  };

  return (
    <FlexBox direction="column" gap={2} sx={{ margin: theme.spacing(4, 0) }}>
      <Typography variant="display0Bold" component={"h2"}>
        {title}
      </Typography>

      <FlexBox direction="column">
        <Typography variant="h2" component={"pre"}>
          {currentSettingDef.description}
        </Typography>
      </FlexBox>

      {isCurrentSettingBoolean && (
        <FlexBox direction="row" alignItems="center" gap={2}>
          <Typography variant="body1">Set for all subaccounts:</Typography>
          <RhfSwitchField
            control={control}
            name="bulkValue"
            label=""
            onChange={event => handleBulkChange(event.target.checked)}
          />
        </FlexBox>
      )}

      <Box
        sx={{ backgroundColor: palette.white, padding: theme.spacing(2, 5) }}
      >
        <Table<ListSubaccountOwnerFieldsQuery["listSubaccounts"][0]>
          rows={subaccounts}
          columns={[
            {
              key: "name",
              renderValue: (row: { name: string }) => row.name,
              title: "Subaccount",
            },
            currentColumn,
          ]}
          noFooter
        />
        <FlexBox sx={{ padding: theme.spacing(4, 0) }}>
          <Button
            variant="contained"
            disabled={!formState.isDirty}
            onClick={handleSubmit(bulkUpdateSettings)}
          >
            Save
          </Button>
        </FlexBox>
      </Box>
    </FlexBox>
  );
}

export function OrganizationSettings(): ReactElement {
  const getErrorMessage = useErrorMessageFormatter();
  const tenant = useTenant()!;
  const isStaff = useCurrentUserIsStaff();
  const snackbar = useSnackbar();
  const client = useNonCacheClearingApolloClient();
  const confirm = useConfirm();
  const enableApprovalsMode = useFeatureFlag("enableApprovalsMode");
  const usePricingV2 = useFeatureFlag("usePricingV2");
  const enableDataToWin = useFeatureFlag("enableDataToWin");

  useBreadcrumbs([
    {
      label: "Organization Settings",
      path: "/organization/settings",
    },
  ]);

  const {
    loading: listSubaccountsLoading,
    error: listSubaccountsError,
    data: listSubaccountsData,
  } = useQuery<ListSubaccountOwnerFieldsQuery>(LIST_SUBACCOUNT_OWNER_FIELDS);

  const [editTenantSettings] = useMutation<
    EditTenantSettingsMutation,
    EditTenantSettingsMutationVariables
  >(EDIT_TENANT_SETTINGS, {
    onErrorSnackbarMessage: "Failed to update organization settings",
    client,
  });

  const [editTenantAutoMMSBreakpoint] = useMutation<
    EditTenantAutoMmsBreakpointMutation,
    EditTenantAutoMmsBreakpointMutationVariables
  >(EDIT_TENANT_AUTO_MMS_BREAKPOINT, {
    onErrorSnackbarMessage: "Failed to update organization auto-MMS breakpoint",
    client,
  });

  const {
    data: staffTenantData,
    loading: staffTenantDataLoading,
    error: staffTenantDataError,
    startPolling: startPollingStaffTenantData,
    stopPolling: stopPollingStaffTenantData,
  } = useQuery<GetTenantForStaffQuery, GetTenantForStaffQueryVariables>(
    GET_TENANT_FOR_STAFF,
    { skip: !isStaff }
  );

  const [
    releaseTenantPhoneNumbers,
    { loading: releaseTenantPhoneNumbersLoading },
  ] = useMutation<
    ReleaseTenantPhoneNumbersMutation,
    ReleaseAssignmentsMutationVariables
  >(RELEASE_TENANT_PHONE_NUMBERS, {
    onErrorSnackbarMessage: "Failed to release organization phone numbers",
    refetchQueries: isStaff ? [GET_TENANT_FOR_STAFF] : [],
    awaitRefetchQueries: true,
  });

  const handleReleaseTenantPhoneNumbers = async () => {
    const { confirmed } = await confirm({
      title: "Release all tenant phone numbers",
      description:
        "Are you sure you want to release all tenant phone numbers? This is not reversible.",
    });
    if (!confirmed) {
      return;
    }

    await releaseTenantPhoneNumbers();
  };

  const releaseTenantPhoneNumbersInProgress =
    releaseTenantPhoneNumbersLoading ||
    (staffTenantData?.getTenant.releasePhoneNumbersJob &&
      jobInProgress(staffTenantData.getTenant.releasePhoneNumbersJob));

  useEffect(() => {
    if (releaseTenantPhoneNumbersInProgress) {
      startPollingStaffTenantData(1000);
    } else {
      stopPollingStaffTenantData();
    }

    return () => stopPollingStaffTenantData();
  });

  return (
    <PageContainer maxWidth="lg">
      <PageHeader>Organization Settings</PageHeader>
      <Formik
        initialValues={{
          [submitErrorField]: null,
          name: tenant.name,
          mfaMinimumRole: tenant.mfaMinimumRole,
        }}
        validationSchema={Yup.object().shape({
          name: Yup.string().required("Name is required"),
          mfaMinimumRole: Yup.string()
            .oneOf(["TEXTER", "SUPERVOLUNTEER"])
            .required("MFA setting is required"),
        })}
        onSubmit={async (values, formik) => {
          const { errors, data } = await editTenantSettings({
            variables: {
              input: {
                name: values.name,
                mfaMinimumRole: values.mfaMinimumRole,
              },
            },
          });

          if (errors) {
            formik.setErrors({
              [submitErrorField]: getErrorMessage(errors),
            });
          } else if (data) {
            formik.resetForm({
              values: {
                [submitErrorField]: null,
                name: data!.editTenantSettings.name,
                mfaMinimumRole: data!.editTenantSettings.mfaMinimumRole,
              },
            });
            snackbar.enqueueSnackbar("Organization settings updated", {
              variant: "success",
            });
          }
        }}
      >
        <Form>
          <FormikErrorBanner />
          <FormikTextField
            name="name"
            label="Name"
            fullWidth
            inputProps={{ "data-testid": "edit-tenant-name-field" }}
          />

          <FormikSelect
            fullWidth
            name="mfaMinimumRole"
            label="Texter Two Factor Authentication"
          >
            <MenuItem value="SUPERVOLUNTEER">
              Disable Two Factor Authentication for Texters
            </MenuItem>
            <MenuItem value="TEXTER">
              Enable Two Factor Authentication for Texters
            </MenuItem>
          </FormikSelect>

          <FormikSubmitButton
            color="primary"
            variant="contained"
            submitText="Save"
            loadingText="Saving..."
            data-testid="save-tenant-basics-button"
          />
        </Form>
      </Formik>
      <Box sx={{ pt: 2 }}>
        <CampaignMetadataFieldsSection />
      </Box>
      <AsyncWrapper
        loading={listSubaccountsLoading}
        errors={listSubaccountsError}
        data={listSubaccountsData}
      >
        {({ listSubaccounts: subaccountData }) => {
          return (
            <>
              <SubaccountSettingsTable
                subaccounts={subaccountData}
                selectedSetting="donationHistoryPage"
                title="Donation History Features"
              />
              {enableApprovalsMode && (
                <SubaccountSettingsTable
                  subaccounts={subaccountData}
                  selectedSetting="approvalsMode"
                  title="Approver Features"
                />
              )}
              {enableDataToWin && (
                <DataToWinOptInTable subaccounts={subaccountData} />
              )}
            </>
          );
        }}
      </AsyncWrapper>
      {isStaff && usePricingV2 && <PricingSection />}
      {isStaff && (
        <Card sx={{ marginTop: 4 }}>
          <CardHeader title="Staff-Only Settings" />
          <CardContent>
            <Formik
              initialValues={{
                [submitErrorField]: null,
                autoMMSBreakpoint: tenant.autoMMSBreakpoint || "",
              }}
              validationSchema={Yup.object().shape({
                autoMMSBreakpoint: Yup.number()
                  .typeError("Enter a number")
                  .min(1, "Must be at least 1")
                  .max(20, "Must be at most 20"),
              })}
              onSubmit={async (values, formik) => {
                const { errors, data } = await editTenantAutoMMSBreakpoint({
                  variables: {
                    input: {
                      autoMMSBreakpoint: values.autoMMSBreakpoint
                        ? Number(values.autoMMSBreakpoint)
                        : null,
                    },
                  },
                });

                if (errors) {
                  formik.setErrors({
                    [submitErrorField]: getErrorMessage(errors),
                  });
                } else if (data) {
                  formik.resetForm({
                    values: {
                      [submitErrorField]: null,
                      autoMMSBreakpoint:
                        data!.editTenantAutoMMSBreakpoint.autoMMSBreakpoint ||
                        "",
                    },
                  });
                  snackbar.enqueueSnackbar(
                    "Organization MMS Breakpoint updated",
                    {
                      variant: "success",
                    }
                  );
                }
              }}
            >
              <Form>
                <FormikErrorBanner />
                <FormikTextField
                  name="autoMMSBreakpoint"
                  label={`Auto-MMS Breakpoint Override (leave blank for default: ${DEFAULT_AUTO_MMS_BREAKPOINT})`}
                  fullWidth
                />

                <FormikSubmitButton
                  color="primary"
                  variant="contained"
                  submitText="Save"
                  loadingText="Saving..."
                  data-testid="save-tenant-basics-button"
                />
              </Form>
            </Formik>
            <AsyncWrapper
              data={staffTenantData}
              loading={staffTenantDataLoading}
              errors={staffTenantDataError}
            >
              {data => {
                if (releaseTenantPhoneNumbersInProgress) {
                  return <Typography>Releasing phone numbers...</Typography>;
                }

                return (
                  <>
                    <Button
                      variant="contained"
                      color="error"
                      sx={{ marginTop: 3 }}
                      onClick={handleReleaseTenantPhoneNumbers}
                    >
                      Release All Phone Numbers
                    </Button>
                    {data.getTenant.releasePhoneNumbersJob && (
                      <Typography sx={{ marginTop: 1 }}>
                        Phone number release at{" "}
                        {formatDateTime(
                          data.getTenant.releasePhoneNumbersJob.createdAt
                        )}{" "}
                        has status{" "}
                        {data.getTenant.releasePhoneNumbersJob.status}.
                      </Typography>
                    )}
                  </>
                );
              }}
            </AsyncWrapper>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}
